import type {
    AIFillerWordsAmbiguousCandidatePayload,
    AIFillerWordsDecisionPayload,
} from "../ai/fillerWords";
import {
    FillerWordMatch,
    REPEATED_WORD_CATEGORY_ID,
    findFillerMatchAt,
    normalizeWord,
} from "./fillerWord";

export type FillerWordPipelineWord = {
    text: string;
    tokenId?: string;
    startSec?: number;
    endSec?: number;
    sourceIndex?: number;
    speaker?: string;
    speakerId?: string;
};

export type FillerWordLocalDecision = "auto_remove" | "auto_keep" | "ambiguous";

export type FillerWordLocalCandidate = {
    id: string;
    categoryId: string;
    firstIndex: number;
    lastIndex: number;
    startTokenId: string;
    endTokenId: string;
    startSec: number;
    endSec: number;
    sourceIndex: number;
    matchedText: string;
    isRepeatedWord: boolean;
    decision: FillerWordLocalDecision;
    decisionReason: string;
    contextLeft: string;
    contextRight: string;
    features: {
        clauseInitial: boolean;
        sentenceInitial: boolean;
        utteranceFinal: boolean;
        standalone: boolean;
        repeatedWord: boolean;
        repetitionLength?: number;
        previousToken?: string;
        nextToken?: string;
    };
};

type MutableCandidate = {
    categoryId: string;
    firstIndex: number;
    lastIndex: number;
    isRepeatedWord: boolean;
    repetitionLength?: number;
};

const MAX_REPEATED_PHRASE_WORDS = 3;
const AUTO_REMOVE_CATEGORY_IDS = new Set(["uh", "um"]);
const STANDALONE_ACK_CATEGORY_IDS = new Set(["hm", "mmm", "mm_hmm"]);
const RESPONSE_RIGHT_KEEP_WORDS = new Set(["right", "okay", "ok", "yes", "yeah"]);
const SUBJECT_PRONOUNS = new Set(["i", "we", "you", "they", "he", "she", "it"]);
const LIKE_KEEP_PREV_WORDS = new Set([
    "would",
    "could",
    "should",
    "feel",
    "feels",
    "felt",
    "look",
    "looks",
    "looked",
    "sound",
    "sounds",
    "sounded",
    "seem",
    "seems",
    "seemed",
]);
const LIKE_KEEP_PREV_BIGRAMS = new Set([
    "i would",
    "we would",
    "you would",
    "they would",
    "he would",
    "she would",
]);
const REPEATED_AUTO_REMOVE_WORDS = new Set([
    "a",
    "an",
    "and",
    "but",
    "i",
    "in",
    "it",
    "my",
    "of",
    "or",
    "the",
    "this",
    "that",
    "to",
    "we",
    "you",
]);
const REPEATED_AUTO_KEEP_WORDS = new Set([
    "go",
    "ha",
    "haha",
    "no",
    "okay",
    "ok",
    "very",
    "why",
    "yes",
]);

const noSpaceBefore = (text: string): boolean => /^[.,!?;:)\]]/.test(text);
const hasQuestionPunctuation = (text: string): boolean => /\?["')\]]*$/.test(String(text ?? "").trim());
const hasTerminalPunctuation = (text: string): boolean => /[.!?]["')\]]*$/.test(String(text ?? "").trim());
const hasCommaBoundary = (text: string): boolean => /[,;:]["')\]]*$/.test(String(text ?? "").trim());
const hasStrongRepeatBoundary = (text: string): boolean => /[.!?;:]["')\]]*$/.test(String(text ?? "").trim());

// Preserve raw token punctuation in candidate/context strings sent to the server.
const joinWordsAsText = (words: Array<{ text: string }>): string => {
    let out = "";
    for (const word of words) {
        const text = String(word?.text ?? "").trim();
        if (!text) continue;
        if (out && !noSpaceBefore(text)) out += " ";
        out += text;
    }
    return out.trim();
};

const getSpeakerKey = (word: FillerWordPipelineWord | undefined): string =>
    String(word?.speakerId ?? word?.speaker ?? "").trim().toLowerCase();

const isSameTurn = (
    left: FillerWordPipelineWord | undefined,
    right: FillerWordPipelineWord | undefined,
): boolean => {
    if (!left || !right) return false;
    const leftSource = Number(left.sourceIndex ?? 0);
    const rightSource = Number(right.sourceIndex ?? 0);
    if (leftSource !== rightSource) return false;
    const leftSpeaker = getSpeakerKey(left);
    const rightSpeaker = getSpeakerKey(right);
    if (!leftSpeaker || !rightSpeaker) return true;
    return leftSpeaker === rightSpeaker;
};

const shouldBlockRepeatedPhraseAcrossBoundary = (
    words: FillerWordPipelineWord[],
    leftEndIndex: number,
    rightStartIndex: number,
): boolean => {
    const leftWord = words[leftEndIndex];
    const rightWord = words[rightStartIndex];
    if (!leftWord || !rightWord) return true;
    if (!isSameTurn(leftWord, rightWord)) return true;
    return hasStrongRepeatBoundary(leftWord.text ?? "");
};

const endsSentence = (text: string): boolean => /[.!?]["')\]]*$/.test(String(text ?? "").trim());
const endsClause = (text: string): boolean => /[,;:!?]["')\]]*$/.test(String(text ?? "").trim()) || endsSentence(text);

const getNormalizedWord = (
    words: FillerWordPipelineWord[],
    index: number,
): string => normalizeWord(words[index]?.text ?? "");

const buildContextText = (
    words: FillerWordPipelineWord[],
    startIndex: number,
    endIndex: number,
    direction: "left" | "right",
    limit: number,
): string => {
    const anchor = words[startIndex];
    if (!anchor) return "";

    const slice: FillerWordPipelineWord[] = [];
    if (direction === "left") {
        for (let i = startIndex - 1; i >= 0 && slice.length < limit; i--) {
            if (!isSameTurn(words[i], anchor)) break;
            slice.unshift(words[i]);
        }
    } else {
        const tail = words[endIndex];
        for (let i = endIndex + 1; i < words.length && slice.length < limit; i++) {
            if (!isSameTurn(tail, words[i])) break;
            slice.push(words[i]);
        }
    }

    return joinWordsAsText(slice);
};

const resolveCandidateOverlap = (
    input: MutableCandidate[],
): MutableCandidate[] => {
    const sorted = [...input].sort((a, b) => {
        if (a.firstIndex !== b.firstIndex) return a.firstIndex - b.firstIndex;
        const lenA = a.lastIndex - a.firstIndex;
        const lenB = b.lastIndex - b.firstIndex;
        if (lenA !== lenB) return lenB - lenA;
        if (a.isRepeatedWord !== b.isRepeatedWord) return a.isRepeatedWord ? -1 : 1;
        return a.categoryId.localeCompare(b.categoryId);
    });

    const kept: MutableCandidate[] = [];
    for (const candidate of sorted) {
        const overlaps = kept.some(
            (existing) =>
                candidate.firstIndex <= existing.lastIndex &&
                candidate.lastIndex >= existing.firstIndex,
        );
        if (!overlaps) kept.push(candidate);
    }

    kept.sort((a, b) => a.firstIndex - b.firstIndex);
    return kept;
};

const detectRepeatedWordCandidates = (
    words: FillerWordPipelineWord[],
): MutableCandidate[] => {
    const out: MutableCandidate[] = [];

    for (let i = 0; i < words.length; i++) {
        const maxPhraseLength = Math.min(
            MAX_REPEATED_PHRASE_WORDS,
            Math.floor((words.length - i) / 2),
        );

        let matched = false;
        for (let phraseLength = maxPhraseLength; phraseLength >= 1; phraseLength--) {
            const firstEnd = i + phraseLength - 1;
            const secondStart = firstEnd + 1;
            const secondEnd = secondStart + phraseLength - 1;
            if (secondEnd >= words.length) continue;
            if (shouldBlockRepeatedPhraseAcrossBoundary(words, firstEnd, secondStart)) {
                continue;
            }

            let allMatch = true;
            for (let offset = 0; offset < phraseLength; offset++) {
                const leftIndex = i + offset;
                const rightIndex = secondStart + offset;
                const leftWord = words[leftIndex];
                const rightWord = words[rightIndex];
                const leftNormalized = getNormalizedWord(words, leftIndex);
                const rightNormalized = getNormalizedWord(words, rightIndex);

                if (!leftNormalized || leftNormalized !== rightNormalized) {
                    allMatch = false;
                    break;
                }
                if (!isSameTurn(leftWord, rightWord)) {
                    allMatch = false;
                    break;
                }
                if (offset > 0 && !isSameTurn(words[leftIndex - 1], leftWord)) {
                    allMatch = false;
                    break;
                }
                if (offset > 0 && !isSameTurn(words[rightIndex - 1], rightWord)) {
                    allMatch = false;
                    break;
                }
            }

            if (!allMatch) continue;

            out.push({
                categoryId: REPEATED_WORD_CATEGORY_ID,
                firstIndex: secondStart,
                lastIndex: secondEnd,
                isRepeatedWord: true,
                repetitionLength: phraseLength,
            });
            i = secondStart - 1;
            matched = true;
            break;
        }
        if (matched) continue;
    }

    return out;
};

const classifyLocalCandidate = (
    words: FillerWordPipelineWord[],
    candidate: MutableCandidate,
): Pick<FillerWordLocalCandidate, "decision" | "decisionReason" | "features"> => {
    const firstWord = words[candidate.firstIndex];
    const lastWord = words[candidate.lastIndex];
    const prevWord = words[candidate.firstIndex - 1];
    const nextWord = words[candidate.lastIndex + 1];
    const prevNormalized = getNormalizedWord(words, candidate.firstIndex - 1);
    const prevPrevNormalized = getNormalizedWord(words, candidate.firstIndex - 2);
    const nextNormalized = getNormalizedWord(words, candidate.lastIndex + 1);
    const turnStartIndex = (() => {
        let idx = candidate.firstIndex;
        while (idx > 0 && isSameTurn(words[idx - 1], words[idx])) idx -= 1;
        return idx;
    })();
    const turnEndIndex = (() => {
        let idx = candidate.lastIndex;
        while (idx + 1 < words.length && isSameTurn(words[idx], words[idx + 1])) idx += 1;
        return idx;
    })();
    const turnWordCount = Math.max(1, turnEndIndex - turnStartIndex + 1);

    const sentenceInitial =
        !isSameTurn(prevWord, firstWord) || endsSentence(prevWord?.text ?? "");
    const clauseInitial =
        !isSameTurn(prevWord, firstWord) || endsClause(prevWord?.text ?? "");
    const utteranceFinal =
        !isSameTurn(lastWord, nextWord) || endsSentence(lastWord?.text ?? "") || !nextWord;
    const standalone =
        (!prevWord || !isSameTurn(prevWord, firstWord) || endsSentence(prevWord.text)) &&
        (!nextWord || !isSameTurn(lastWord, nextWord) || endsSentence(lastWord.text));
    const commaBounded =
        hasCommaBoundary(prevWord?.text ?? "") ||
        hasCommaBoundary(firstWord?.text ?? "") ||
        hasCommaBoundary(lastWord?.text ?? "");
    const questionAdjacent =
        hasQuestionPunctuation(firstWord?.text ?? "") ||
        hasQuestionPunctuation(lastWord?.text ?? "") ||
        hasQuestionPunctuation(nextWord?.text ?? "");
    const shortResponseTurn = turnWordCount <= 3;
    const likelyLexicalLike =
        candidate.categoryId === "like" &&
        !commaBounded &&
        !clauseInitial &&
        !standalone &&
        Boolean(nextNormalized) &&
        (
            nextNormalized === "to" ||
            SUBJECT_PRONOUNS.has(prevNormalized) ||
            LIKE_KEEP_PREV_WORDS.has(prevNormalized) ||
            LIKE_KEEP_PREV_BIGRAMS.has([prevPrevNormalized, prevNormalized].filter(Boolean).join(" "))
        );

    const features = {
        clauseInitial,
        sentenceInitial,
        utteranceFinal,
        standalone,
        commaBounded,
        questionAdjacent,
        shortResponseTurn,
        turnWordCount,
        likelyLexicalLike,
        repeatedWord: candidate.isRepeatedWord,
        repetitionLength: candidate.repetitionLength,
        previousToken: prevNormalized || undefined,
        nextToken: nextNormalized || undefined,
    };

    if (AUTO_REMOVE_CATEGORY_IDS.has(candidate.categoryId)) {
        return {
            decision: "auto_remove",
            decisionReason: "Core hesitation filler.",
            features,
        };
    }

    if (candidate.isRepeatedWord) {
        const repeatedTokens = words
            .slice(candidate.firstIndex, candidate.lastIndex + 1)
            .map((word) => normalizeWord(word.text))
            .filter(Boolean);
        const allKeepTokens = repeatedTokens.length > 0 && repeatedTokens.every((token) => REPEATED_AUTO_KEEP_WORDS.has(token));
        const allRemoveTokens = repeatedTokens.length > 0 && repeatedTokens.every((token) => REPEATED_AUTO_REMOVE_WORDS.has(token));

        if (allKeepTokens) {
            return {
                decision: "auto_keep",
                decisionReason: "Likely emphatic repetition.",
                features,
            };
        }

        if (allRemoveTokens && !!nextNormalized) {
            return {
                decision: "auto_remove",
                decisionReason: "Likely adjacent stutter repetition.",
                features,
            };
        }

        return {
            decision: "ambiguous",
            decisionReason: "Repeated phrase needs context review.",
            features,
        };
    }

    if (STANDALONE_ACK_CATEGORY_IDS.has(candidate.categoryId) && standalone) {
        return {
            decision: "auto_keep",
            decisionReason: "Standalone acknowledgement or response cue.",
            features,
        };
    }

    if (candidate.categoryId === "mm_hmm" && shortResponseTurn) {
        return {
            decision: "auto_keep",
            decisionReason: "Short response-turn acknowledgement.",
            features,
        };
    }

    if (
        candidate.categoryId === "right" &&
        (
            questionAdjacent ||
            (utteranceFinal && (hasTerminalPunctuation(lastWord?.text ?? "") || standalone || !isSameTurn(lastWord, nextWord))) ||
            RESPONSE_RIGHT_KEEP_WORDS.has(nextNormalized)
        )
    ) {
        return {
            decision: "auto_keep",
            decisionReason: "Likely acknowledgement or confirmation.",
            features,
        };
    }

    if (candidate.categoryId === "like") {
        if (likelyLexicalLike) {
            return {
                decision: "auto_keep",
                decisionReason: "Likely lexical use of 'like', not filler.",
                features,
            };
        }
    }

    if (candidate.categoryId === "well" && standalone) {
        return {
            decision: "auto_keep",
            decisionReason: "Standalone response framing.",
            features,
        };
    }

    return {
        decision: "ambiguous",
        decisionReason: "Needs contextual review.",
        features,
    };
};

const toCandidateId = (
    categoryId: string,
    startTokenId: string,
    endTokenId: string,
): string => `filler_${categoryId}_${startTokenId}_${endTokenId}`;

export const buildLocalFillerWordCandidates = (
    words: FillerWordPipelineWord[],
    enabledCategories: Set<string>,
): FillerWordLocalCandidate[] => {
    if (!Array.isArray(words) || words.length === 0 || enabledCategories.size === 0) {
        return [];
    }

    const mutable: MutableCandidate[] = [];
    const repeatedProtectedRanges: Array<{ start: number; end: number }> = [];

    if (enabledCategories.has(REPEATED_WORD_CATEGORY_ID)) {
        const repeated = detectRepeatedWordCandidates(words);
        mutable.push(...repeated);
        for (const candidate of repeated) {
            repeatedProtectedRanges.push({
                start: candidate.firstIndex,
                end: candidate.lastIndex,
            });
        }
    }

    for (let i = 0; i < words.length; i++) {
        const protectedByRepeat = repeatedProtectedRanges.some(
            (range) => i >= range.start && i <= range.end,
        );
        if (protectedByRepeat) continue;

        const match: FillerWordMatch | null = findFillerMatchAt(words, i, enabledCategories);
        if (!match) continue;

        mutable.push({
            categoryId: match.categoryId,
            firstIndex: i,
            lastIndex: i + Math.max(1, match.wordCount) - 1,
            isRepeatedWord: false,
        });
        i += Math.max(0, match.wordCount - 1);
    }

    const resolved = resolveCandidateOverlap(mutable);
    return resolved
        .map((candidate) => {
            const matchedWords = words.slice(candidate.firstIndex, candidate.lastIndex + 1);
            const firstWord = matchedWords[0];
            const lastWord = matchedWords[matchedWords.length - 1];
            if (!firstWord || !lastWord) return null;

            const classified = classifyLocalCandidate(words, candidate);
            return {
                id: toCandidateId(
                    candidate.categoryId,
                    String(firstWord.tokenId ?? ""),
                    String(lastWord.tokenId ?? ""),
                ),
                categoryId: candidate.categoryId,
                firstIndex: candidate.firstIndex,
                lastIndex: candidate.lastIndex,
                startTokenId: String(firstWord.tokenId ?? ""),
                endTokenId: String(lastWord.tokenId ?? ""),
                startSec: Number(firstWord.startSec ?? 0),
                endSec: Number(lastWord.endSec ?? firstWord.endSec ?? firstWord.startSec ?? 0),
                sourceIndex: Number(firstWord.sourceIndex ?? 0),
                matchedText: joinWordsAsText(matchedWords),
                isRepeatedWord: candidate.isRepeatedWord,
                decision: classified.decision,
                decisionReason: classified.decisionReason,
                contextLeft: buildContextText(words, candidate.firstIndex, candidate.lastIndex, "left", 10),
                contextRight: buildContextText(words, candidate.firstIndex, candidate.lastIndex, "right", 10),
                features: classified.features,
            } satisfies FillerWordLocalCandidate;
        })
        .filter((candidate): candidate is FillerWordLocalCandidate => Boolean(candidate));
};

export const buildAmbiguousFillerWordRequests = (
    candidates: FillerWordLocalCandidate[],
): AIFillerWordsAmbiguousCandidatePayload[] =>
    candidates
        .filter((candidate) => candidate.decision === "ambiguous")
        .map((candidate) => ({
            id: candidate.id,
            categoryId: candidate.categoryId,
            startTokenId: candidate.startTokenId,
            endTokenId: candidate.endTokenId,
            startSec: candidate.startSec,
            endSec: candidate.endSec,
            sourceIndex: candidate.sourceIndex,
            matchedText: candidate.matchedText,
            contextLeft: candidate.contextLeft,
            contextRight: candidate.contextRight,
            features: candidate.features,
        }));

export const mergeFillerWordServerDecisions = (
    candidates: FillerWordLocalCandidate[],
    decisions: AIFillerWordsDecisionPayload[],
): FillerWordLocalCandidate[] => {
    const byId = new Map(
        (Array.isArray(decisions) ? decisions : []).map((decision) => [
            String(decision?.id ?? "").trim(),
            String(decision?.decision ?? "").trim().toUpperCase(),
        ]),
    );

    return candidates.filter((candidate) => {
        if (candidate.decision === "auto_keep") return false;
        if (candidate.decision === "auto_remove") return true;

        const serverDecision = byId.get(candidate.id);
        return serverDecision === "REMOVE";
    });
};
